import idx from 'idx'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import withMixpanel, { TRAP_DELETED, SCREEN_VIEW_MANAGE_TRAPS_TRAPS } from 'utils/mixpanel'
import { compose, withHandlers, withStateHandlers, lifecycle } from 'recompose'
import { isEqual, getPaginationObject, filterActiveTraps } from 'utils/misc'

import * as trapModule from 'modules/trap'
import * as toastModule from 'modules/toast'
import * as popoverModule from 'modules/popover'
import * as userActions from 'actions/userActions'
import * as routeActions from 'actions/routeActions'

export default compose(
  withMixpanel,
  connect(
    (state) => {
      return {
        user: idx(state, _ => _.user),
        traps: idx(state, _ => _.traps.items) && filterActiveTraps(idx(state, _ => _.traps.items)),
        tableLoading: idx(state, _ => _.groups.loading),
        organisation: idx(state, _ => _.organisation.selected),
      }
    },
    (dispatch) => {
      const actions = {
        ...trapModule,
        ...userActions,
        ...toastModule,
        ...routeActions,
        ...popoverModule,
      }

      return {
        actions: bindActionCreators(actions, dispatch),
      }
    },
  ),
  withStateHandlers(
    () => ({
      selectedTraps: [],
      bulkSelected: false,
      filters: {
        search: null,
        pagination: {
          offset: 0, limit: 8
        },
        active: true,
        loaded: true,
      }
    }),
    {
      onPageChange: state => (offset) => {
        return getPaginationObject(state, { offset }, false)
      },
      setPagination: state => ({ count }) => {
        return getPaginationObject(state, { count }, true)
      },
      setFilters: state => newFilters => ({
        ...state,
        filters: {
          ...state.filters,
          ...newFilters,
          loaded: false,
        }
      }),
      setState: state => changedValues => ({
        ...state,
        ...changedValues
      }),
      setAllTraps: ({ bulkSelected }) => selectedTraps => ({
        bulkSelected: !bulkSelected,
        selectedTraps: bulkSelected ? [] : selectedTraps,
      }),
      unsetAllTraps: () => () => ({
        bulkSelected: false,
        selectedTraps: [],
      }),
    }
  ),
  withHandlers({
    handleTrapsAssign: ({
      actions, setState, selectedTraps, organisation, filters, setPagination
    }) => () => {
      actions.hideToast()
      actions.showPopover(popoverModule.ASSIGN_TRAPS_GROUPS, {
        selectedTraps,
        organisation,
        onConfirm: (selectedGroup) => {
          actions.hidePopover()
          setState({ selectedTraps: [] })
          actions.assignTrapsToGroup(organisation, selectedTraps, selectedGroup, () => {
            actions.getReportTraps(organisation, filters, setPagination)
          })
        }
      })
    },
    handleProjectTransfer: ({
      actions, setState, selectedTraps, organisation, filters, setPagination
    }) => () => {
      actions.hideToast()
      actions.showPopover(popoverModule.TRANSFER_TRAPS_PROJECT, {
        selectedTraps,
        organisation,
        onConfirm: async (selectedOrganisation) => {
          actions.hidePopover()
          setState({ selectedTraps: [] })
          try { 
            await actions.transferTrapsToProject(selectedTraps, selectedOrganisation.uuid)
            actions.showToast(toastModule.SUCCESS_TOAST, 'Trap transferred!')
            actions.getReportTraps(organisation, filters, setPagination)
          // eslint-disable-next-line
          } catch (error) {}
        }
      })
    },
  }),
  withHandlers({
    onSearchChange: ({ setFilters }) => (value) => {
      setFilters({ search: [{ key: 'trapName', value }] })
    },
    onTableSortClick: ({ setFilters }) => (orderBy) => {
      setFilters({ orderBy: [orderBy] })
    },
    onTableRowClick: ({ 
      track, actions, organisation, filters, setPagination 
    }) => (trap) => {
      actions.showPopover(popoverModule.EDIT_TRAP, {
        ...trap,
        onDelete: () => {
          actions.hidePopover()
          actions.showPopover(popoverModule.DELETE_TRAP_CONFIRM, {
            hideCloseButton: true,
            onConfirm: () => {
              actions.hidePopover()
              actions.deleteTrap(organisation, trap, () => {
                track(TRAP_DELETED)
                actions.getReportTraps(organisation, filters, setPagination)
              })
            }
          })
        },
        onUpdate: (values) => {
          const newValues = {
            name: values.name,
            location: {
              latitude: values.latitude,
              longitude: values.longitude
            }
          }
          actions.updateTrap(organisation, trap, newValues, () => {
            actions.hidePopover()
            actions.getReportTraps(organisation, filters, setPagination)
          })
        }
      })
    },
    onTableSelectableClick: ({ setState, ...state }) => (trap) => {
      const selectedTraps = [...state.selectedTraps]
      const trapIndex = selectedTraps.indexOf(trap.historyUuid)
      if (trapIndex > -1) {
        selectedTraps.splice(trapIndex, 1)
      } else {
        selectedTraps.push(trap.historyUuid)
      }
      setState({
        selectedTraps,
        bulkSelected: false,
      })
    },
    onBulkSelect: ({ setAllTraps, traps }) => () => {
      setAllTraps(Object.keys(traps))
    },
    handleSelectedChange: ({
      actions, selectedTraps, handleTrapsAssign, handleProjectTransfer
    }) => () => {
      actions.showToast(toastModule.ACTION_BUTTON_TOAST, {
        manual: true,
        hasTwoActions: true,
        actionLabel: 'Assign to group',
        label: `${selectedTraps.length} trap(s) selected`,
        onActionClick: () => handleTrapsAssign(),
        secondActionLabel: 'Transfer to project',
        secondOnActionClick: () => handleProjectTransfer()
      })
    },
    onShareClick: ({ 
      actions,
      organisation, 
      filters, 
      setPagination
    }) => (trap) => {
      actions.showPopover(popoverModule.SHARE_TRAP_INDIVIDUAL, {
        ...trap,
        organisation, 
        filters, 
        setPagination
      })
    },
  }),
  lifecycle({
    componentDidMount() {
      const { 
        track, actions, organisation, filters, setPagination
      } = this.props

      track(SCREEN_VIEW_MANAGE_TRAPS_TRAPS)
      actions.getReportTraps(organisation, filters, setPagination)
    },
    componentDidUpdate(prevProps) {
      const { 
        actions, organisation, filters, setPagination, selectedTraps, handleSelectedChange
      } = this.props

      if (selectedTraps.length !== prevProps.selectedTraps.length) {
        if (selectedTraps.length === 0) {
          actions.hideToast()
        } else {
          handleSelectedChange()  
        }
      }

      if (!isEqual(filters, prevProps.filters) && !filters.loaded) {
        actions.getReportTraps(organisation, filters, setPagination)
      }
    }
  })
)




// WEBPACK FOOTER //
// ./src/containers/traps/ManageTraps/ManageTraps.enhancer.js